/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Price range bar — non-price filter buttons (list filters, reset)
document.addEventListener('DOMContentLoaded', function () {
  var productFacet = document.querySelector('product-facet');
  if (!productFacet) return;

  productFacet.addEventListener('click', function (e) {
    var btn = e.target.closest('.price-range-btn');
    if (!btn || btn.hasAttribute('data-price-bucket')) return;
    e.preventDefault();
    btn.dispatchEvent(
      new CustomEvent('facet:criteria-changed', { bubbles: true, detail: { url: btn.getAttribute('href') } })
    );
  });
});

// Price bucket multi-select — capture phase fires before theme handlers
document.addEventListener('click', function (e) {
  var btn = e.target.closest('[data-price-bucket]');
  if (!btn || !btn.closest('product-facet')) return;

  e.preventDefault();
  e.stopPropagation();

  // Optimistically toggle so the combined state is immediately correct
  btn.classList.toggle('price-range-btn--active');

  var productFacet = btn.closest('product-facet');
  var allBuckets = Array.from(productFacet.querySelectorAll('[data-price-bucket]'));
  var baseUrl = btn.dataset.baseUrl;
  var selectedMins = [];
  var selectedMaxes = [];

  allBuckets.forEach(function (b) {
    if (b.classList.contains('price-range-btn--active')) {
      selectedMins.push(parseInt(b.dataset.priceMin, 10));
      selectedMaxes.push(parseInt(b.dataset.priceMax, 10));
    }
  });

  var url;
  if (selectedMins.length === 0) {
    url = baseUrl;
  } else {
    url = baseUrl + '&filter.v.price.gte=' + Math.min.apply(null, selectedMins) + '&filter.v.price.lte=' + Math.max.apply(null, selectedMaxes);
  }

  btn.dispatchEvent(new CustomEvent('facet:criteria-changed', { bubbles: true, detail: { url: url } }));
}, true); // capture phase


// Diwali hamper AJAX navigation (Section Rendering API)
document.addEventListener('click', function (e) {
  const link = e.target.closest('a[data-ajax-hamper]');
  if (!link) return;
  e.preventDefault();

  const handle = link.dataset.ajaxHamper;
  const sectionId = link.dataset.section;
  const url = link.getAttribute('href');
  const sectionEl = document.getElementById('shopify-section-' + sectionId);
  if (!sectionEl || sectionEl.classList.contains('diwali-loading')) return;

  sectionEl.classList.add('diwali-loading');

  fetch('/products/' + handle + '?section_id=' + sectionId)
    .then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    })
    .then(function (html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.getElementById('shopify-section-' + sectionId);
      if (!newSection) return;

      sectionEl.innerHTML = newSection.innerHTML;
      sectionEl.classList.remove('diwali-loading');
      history.pushState({}, '', url);

      // Re-execute scripts so widgets (variant picker, etc.) re-initialize
      sectionEl.querySelectorAll('script').forEach(function (oldScript) {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach(function (attr) {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    })
    .catch(function (err) {
      console.error('[Diwali Hamper Nav]', err);
      sectionEl.classList.remove('diwali-loading');
    });
});

document.querySelector(".box-includes_text")?.addEventListener("click", () => {
  document.getElementById("box-includes-popup").open = true;
});

// Show More and Show Less Product Page

var container = $(".product_description-tab_item");
var readMoreBtn = $(".read-more-btn");
var readLessBtn = $(".read-less-btn");

readMoreBtn.on("click", function () {
  container.css({ "max-height": "none", "-webkit-mask-image": "none" });
  readMoreBtn.hide();
  readLessBtn.show();
});

readLessBtn.on("click", function () {
  container.css({
    "max-height": "300px",
    "-webkit-mask-image": "linear-gradient(180deg, #000 50%, transparent 100%)",
  });
  readMoreBtn.show();
  readLessBtn.hide();
  $("html, body").animate(
    {
      scrollTop: container.offset().top,
    },
    500
  );
});

// Flickity for image-with-text section
// $('.custom-flickity').flickity({
//   // options
//   cellAlign: 'left',
//   contain: true
// });

// play video when on power saving mode
// document.addEventListener('DOMContentLoaded', function () {
//     const videos = document.querySelectorAll('video');
//     videos.forEach(video => {
//         // Check if the video is not playing
//         if (video.paused) {
//           console.log("paused");
//             // Attempt to play the video
//             video.play().catch(error => {
//                 console.error("Error attempting to play video: ", error);
//                 // Handle the error here. For example, show a play button to the user.
//             });
//         }
//     });
// });

// show weight on variant
const variant_weight = document.querySelector(".variant_weight");
if (variant_weight) {
  document.addEventListener("variant:changed", (e) => {
    const weight = e.detail?.variant?.weight ?? null;
    if (weight) {
      variant_weight.innerHTML = `(${weight}g)`;
    }
  });
}

// Desert Box Date Selection

const allButtons = document.querySelectorAll(".db-ds_date_selection div");
const todayButton = document.querySelector(".db-ds__today");
const tomorrowButton = document.querySelector(".db-ds__tomorrow");
const customDateButton = document.querySelector(".db-ds__select_date");
const customDateButtonLabel = document.querySelector(
  ".db-ds__select_date label"
);
const selectedDateInput = document.getElementById("selected_date");
const timeSelectionContainer = document.querySelector(".db-ds_time_selection");
const timeSelectDropdown = document.getElementById("db-ds__select_time");
const errorMessageContainer = document.querySelector(".db-ds__error_message");

const monthNames = {
  January: "JAN",
  February: "FEB",
  March: "MAR",
  April: "APR",
  May: "MAY",
  June: "JUN",
  July: "JUL",
  August: "AUG",
  September: "SEP",
  October: "OCT",
  November: "NOV",
  December: "DEC",
};

if (todayButton && tomorrowButton && customDateButton) {
  const currentTime = new Date();
  const tomorrow = new Date(currentTime);
  tomorrow.setDate(currentTime.getDate() + 1);
  const tomorrowMonthName = tomorrow.toLocaleString("default", {
    month: "long",
  });
  const dayAfterTomorrow = new Date(currentTime);
  dayAfterTomorrow.setDate(currentTime.getDate() + 2);

  document.addEventListener("DOMContentLoaded", () => {
    if (currentTime.getHours() < 13) {
      timeSelectDropdown.innerHTML = `<option value='5pm - 8pm'>5pm - 8pm</option>`;
    } else {
      todayButton.style.display = "none";
      const event = new Event("click");
      tomorrowButton.dispatchEvent(event);
    }

    tomorrowButton.innerHTML = `TOMORROW ${tomorrow.getDate()} ${
      monthNames[tomorrowMonthName]
    }`;

    $("#datePicker").datepicker({
      dateFormat: "dd/mm/yy",
      minDate: dayAfterTomorrow,
      onSelect: (dateStr) => {
        const dateParts = dateStr.split("/");
        const date = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
        console.log(date.getMonth());
        selectedDateInput.value = dateStr;
        allButtons.forEach((btn) => btn.classList.remove("selected"));
        customDateButton.classList.add("selected");
        customDateButtonLabel.innerHTML = `SELECTED DATE ${date.getDate()} ${
          monthNames[date.toLocaleString("default", { month: "long" })]
        }`;
        timeSelectDropdown.innerHTML = `<option value='1pm - 4pm'>1pm - 4pm</option>`;
      },
    });
  });
  todayButton.addEventListener("click", () => {
    if (currentTime.getHours() < 13) {
      customDateButtonLabel.innerHTML = `SELECT DATE`;
      selectedDateInput.value = `Today`;
      timeSelectDropdown.innerHTML = `<option value='5pm - 8pm'>5pm - 8pm</option>`;
    }
  });

  tomorrowButton.addEventListener("click", () => {
    customDateButtonLabel.innerHTML = `SELECT DATE`;
    selectedDateInput.value = `TOMORROW ${tomorrow.getDate()} ${
      monthNames[tomorrowMonthName]
    }`;
    timeSelectDropdown.innerHTML = `<option value='1pm - 4pm'>1pm - 4pm</option>`;
  });

  // add selected class
  allButtons.forEach((button) => {
    button.addEventListener("click", (e) => {
      if (!(e.target.tagName === "INPUT")) {
        allButtons.forEach((btn) => btn.classList.remove("selected"));
        e.target.classList.add("selected");
      }
    });
  });
}
// end of Desert Box Date Selector

// Net weight / unit price. Weight comes from the custom.weight metafield (data-metafield-grams)
// when set, otherwise from the selected variant's weight; unit price is always calculated.
document.addEventListener("variant:changed", function (event) {
  const info = document.querySelector("[data-weight-info]");
  if (!info) return;
  const variant = event.detail && event.detail.variant;
  const metafieldGrams = Number(info.dataset.metafieldGrams) || 0;
  const usingMetafield = metafieldGrams > 0;
  const grams = usingMetafield ? metafieldGrams : variant && Number(variant.weight);
  if (!variant || !grams || grams <= 0) {
    info.style.display = "none";
    return;
  }
  const format =
    (window.themeVariables &&
      window.themeVariables.settings &&
      window.themeVariables.settings.moneyFormat) ||
    "Rs. {{amount}}";
  const perKg = grams >= 1000;
  const cents = Math.round((variant.price * (perKg ? 1000 : 1)) / grams);
  const noDecimals = /amount_no_decimals/.test(format);
  const amount = (cents / 100)
    .toFixed(noDecimals ? 0 : 2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (!usingMetafield) {
    info.querySelector("[data-net-weight]").textContent = perKg
      ? `${Math.round((grams / 1000) * 100) / 100}kg`
      : `${grams}g`;
  }
  info.querySelector("[data-unit-price]").textContent = format.replace(
    /\{\{\s*\w+\s*\}\}/,
    amount
  );
  info.querySelector("[data-unit-label]").textContent = perKg ? "/kg" : "/g";
  info.style.display = "";
});

// Show Variant Description on change
document.addEventListener("variant:changed", function (event) {
  let variant = event.detail.variant;
  const variantDescElements = document.querySelectorAll(".variant_description");
  variantDescElements.forEach((ele) => {
    if (ele.dataset.variantTitle === variant.title) {
      ele.style.display = "block";
    } else {
      ele.style.display = "none";
    }
  });
});

// Handmade Rakhi: keep the "Color" line item property in sync with the selected variant
(function () {
  const rakhiColorInput = document.getElementById("rakhi-color-property");
  if (!rakhiColorInput) return;

  const colorOptionIndex =
    parseInt(rakhiColorInput.dataset.colorOptionIndex, 10) || 0;

  document.addEventListener("variant:changed", function (event) {
    const variant = event.detail?.variant;
    if (!variant) return;

    const color = Array.isArray(variant.options)
      ? variant.options[colorOptionIndex]
      : variant.title;

    if (color) rakhiColorInput.value = color;
  });
})();

// Persist order attributes (delivery date, gift info) to the Shopify cart so
// checkout (Shopflo) sees them. Does not trigger cart:refresh.
// - Calls made in the same tick are merged into one POST (e.g. gift clear + save).
// - Only the changed keys are posted: /cart/update.js merges attributes server-side, so
//   other attributes (Order Note...) are untouched. Unchanged values are skipped, so
//   widget/cart re-renders that re-apply the same values cost nothing.
// - POSTs run one after another, retry once, and failed keys are resent on the next call.
// Resolves with the cart JSON from the POST, or null when nothing was posted / it failed.
let cartAttributesQueue = Promise.resolve();
let cartAttributesPending = {};
let cartAttributesFailed = {};
let cartAttributesWaiters = [];
let cartAttributesScheduled = false;
let cartAttributesInFlight = 0;
let lastSyncedAttributes = null;

document.addEventListener("cart:updated", (event) => {
  const cart = event.detail && event.detail.cart;
  if (cart && cart.attributes) lastSyncedAttributes = { ...cart.attributes };
});

async function postCartAttributes(attributes) {
  for (let attempt = 0; ; attempt++) {
    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      if (attempt >= 1) throw error;
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }
}

function flushCartAttributes() {
  const attributes = { ...cartAttributesFailed, ...cartAttributesPending };
  const waiters = cartAttributesWaiters;
  cartAttributesFailed = {};
  cartAttributesPending = {};
  cartAttributesWaiters = [];
  cartAttributesScheduled = false;
  cartAttributesInFlight++;

  cartAttributesQueue = cartAttributesQueue.then(async () => {
    let cart = null;
    try {
      if (!lastSyncedAttributes) {
        const current = await fetch("/cart.js", {
          headers: { Accept: "application/json" },
        }).then((response) => response.json());
        lastSyncedAttributes = { ...(current.attributes || {}) };
      }
      const changed = {};
      Object.entries(attributes).forEach(([key, value]) => {
        const next = value == null ? "" : String(value);
        if (String(lastSyncedAttributes[key] ?? "") !== next) changed[key] = next;
      });
      if (Object.keys(changed).length) {
        try {
          cart = await postCartAttributes(changed);
          Object.assign(lastSyncedAttributes, changed);
        } catch (error) {
          Object.assign(cartAttributesFailed, changed);
          throw error;
        }
      }
    } catch (error) {
      console.error("syncCartAttributes failed", error);
    } finally {
      cartAttributesInFlight--;
      waiters.forEach((resolve) => resolve(cart));
    }
  });
}

window.syncCartAttributes = (attributes) =>
  new Promise((resolve) => {
    Object.assign(cartAttributesPending, attributes);
    cartAttributesWaiters.push(resolve);
    if (!cartAttributesScheduled) {
      cartAttributesScheduled = true;
      queueMicrotask(flushCartAttributes);
    }
  });

window.hasPendingCartAttributes = () =>
  cartAttributesScheduled || cartAttributesInFlight > 0;
window.cartAttributesSettled = () => cartAttributesQueue;

// initialize gifting widget
window.updateCartPermalinks = (queryParams, orderAttributes) => {
  const cartForm =
    document.querySelector("#mini-cart-form") ||
    document.querySelector("#cart-page-form");
  if (!cartForm) return;

  const url = cartForm.action + "?" + queryParams;

  if (queryParams === "") {
    cartForm.action = "/cart";
  } else {
    cartForm.action = url;
  }

  Object.entries(orderAttributes).forEach(([key, value]) => {
    document
      .querySelectorAll(`[name="${key}"]`)
      .forEach((input) => (input.value = value));
  });

  const cartAttributes = {};
  Object.entries(orderAttributes).forEach(([key, value]) => {
    const match = key.match(/^attributes\[(.+)\]$/);
    if (match) cartAttributes[match[1]] = value;
  });
  window.syncCartAttributes(cartAttributes);
};

function fetchAndInitializeGiftWidget() {
  console.log("called initializeGiftWidget");
  if (window.initializeGiftWidget) {
    window.initializeGiftWidget();
  }
}

// ---- Delivery options (Standard / Choose a delivery date) ----
// The radios live in Shopify (snippets/delivery-options.liquid). Picking
// "Choose a delivery date" mounts the React datepicker (assets/gifting.js) into
// the block's .delivery-datepicker-mount; "Standard" hides it and clears the
// delivery-date cart attribute. The chosen date persists across cart refreshes.

function setAllDeliveryDates(value) {
  document
    .querySelectorAll('[name="attributes[Delivery Date]"]')
    .forEach((input) => {
      input.value = value;
    });
  window.syncCartAttributes({ "Delivery Date": value });
}

// Standard-delivery "Arrives by {date}" estimate, kept client-side only
// (localStorage.standard_delivery_data) — not submitted as an order attribute.
// Choose-a-date carts use "Delivery Date" instead, set by the datepicker in
// assets/gifting.js.
function setAllArrivedBy(value) {
  try {
    if (value) {
      localStorage.setItem("standard_delivery_data", value);
    } else {
      localStorage.removeItem("standard_delivery_data");
    }
  } catch (e) {
    /* no-op */
  }
}

const ARRIVED_BY_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const arrivedByOrdinal = (day) => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

// "yyyy-mm-dd" -> "Sep 25th" — same format the gift-widget datepicker writes
// to "Delivery Date", so both attributes read consistently on an order.
function formatArrivedBy(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  const day = parseInt(m[3], 10);
  const monthIndex = parseInt(m[2], 10) - 1;
  if (monthIndex < 0 || monthIndex > 11 || Number.isNaN(day)) return "";
  return `${ARRIVED_BY_MONTHS[monthIndex]} ${day}${arrivedByOrdinal(day)}`;
}

// Standard delivery selected (and selectable) -> localStorage.standard_delivery_data
// holds the "Arrives by" estimate; "Choose a delivery date" -> cleared (that
// mode's own pick lives in "Delivery Date").
function syncArrivedBy(block) {
  const standard = block.querySelector(
    '.delivery-mode-radio[data-mode="standard"]',
  );
  if (standard && standard.checked && !standard.disabled) {
    setAllArrivedBy(formatArrivedBy(block.dataset.arrivesByIso));
  } else {
    setAllArrivedBy("");
  }
}

function mountDeliveryDatepicker(block) {
  const mount = block.querySelector(".delivery-datepicker-mount");
  if (!mount) return;
  mount.hidden = false;
  if (window.initializeDatePicker) {
    window.initializeDatePicker(mount);
  }
}

// Reflect the checked radio on the option wrappers (border/background).
function reflectDeliverySelection(block) {
  block.querySelectorAll(".delivery-options__option").forEach((opt) => {
    const input = opt.querySelector(".delivery-mode-radio");
    opt.classList.toggle("is-selected", !!input && input.checked);
  });
}

function clearStoredDeliveryDate() {
  setAllDeliveryDates("");
  try {
    localStorage.removeItem("delivery_date_iso");
    localStorage.removeItem("delivery_date_display");
  } catch (e) {
    /* no-op */
  }
}

function applyDeliveryMode(radio) {
  const block = radio.closest("delivery-options");
  if (!block) return;

  clearDeliveryError(block);
  reflectDeliverySelection(block);

  const mode = radio.dataset.mode || radio.value;
  try {
    localStorage.setItem("delivery_mode", mode);
  } catch (e) {
    /* no-op */
  }
  const mount = block.querySelector(".delivery-datepicker-mount");
  if (mode === "choose") {
    mountDeliveryDatepicker(block);
  } else {
    // Standard = no chosen date. Fully unmount the picker so its selection state
    // is discarded (otherwise re-opening "Choose" shows a stale highlighted date
    // while the cleared attribute stays blank), then clear the date everywhere.
    if (mount && window.destroyDatePicker) window.destroyDatePicker(mount);
    if (mount) mount.hidden = true;
    clearStoredDeliveryDate();
    resetChooseSubText();
  }

  syncArrivedBy(block);
}

// Reset every "Choose a delivery date" sub-text back to its default copy —
// clearing the "Delivery on {date}" line when the default copy is empty.
function resetChooseSubText() {
  document.querySelectorAll("[data-delivery-choose-sub]").forEach((el) => {
    el.textContent = el.dataset.defaultSub || "";
  });
}

document.addEventListener("change", (e) => {
  const radio =
    e.target && e.target.closest
      ? e.target.closest(".delivery-mode-radio")
      : null;
  if (radio) applyDeliveryMode(radio);
});

// ---- Checkout validation ----
// Wired on the theme checkout buttons: onclick="return validateDeliveryDate(this)".
// Returns false (blocking the submit) when "Choose a delivery date" is selected
// but no date has been picked, and surfaces an inline error on that cart block.
window.validateDeliveryDate = function (btn) {
  const surface =
    (btn && btn.closest && btn.closest(".shopify-section")) || document;
  const block =
    surface.querySelector("delivery-options") ||
    document.querySelector("delivery-options");
  if (!block) return true;

  const choose = block.querySelector('.delivery-mode-radio[data-mode="choose"]');
  const hasDate = Array.from(
    document.querySelectorAll('[name="attributes[Delivery Date]"]'),
  ).some((i) => (i.value || "").trim() !== "");

  if (choose && choose.checked && !hasDate) {
    const err = block.querySelector("[data-delivery-error]");
    if (err) err.hidden = false;
    const card = block.querySelector(".delivery-options__option--block");
    if (card) card.classList.add("has-error");
    block.scrollIntoView({ behavior: "smooth", block: "center" });
    return false;
  }
  return true;
};

// Shopflo's checkout button (snippets/shopflo.liquid) has no onclick and opens from a
// bubble-phase document listener in assets/shopflo-script.js, so gate it here in the
// capture phase, before that listener runs.
window.addEventListener(
  "click",
  (e) => {
    const btn =
      e.target && e.target.closest
        ? e.target.closest('[data-flo-action="checkout"]')
        : null;
    if (!btn || btn.dataset.floSyncReleased) return;
    if (btn.getAttribute("aria-busy") === "true") {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (!window.validateDeliveryDate(btn)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    // Shopflo reads the cart when the button is clicked, so hold the click until any
    // in-flight delivery-date / gift attribute update has landed (max 3s), then re-click.
    if (window.hasPendingCartAttributes && window.hasPendingCartAttributes()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      btn.setAttribute("aria-busy", "true");
      Promise.race([
        window.cartAttributesSettled(),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]).then(() => {
        btn.removeAttribute("aria-busy");
        btn.dataset.floSyncReleased = "true";
        btn.click();
        delete btn.dataset.floSyncReleased;
      });
    }
  },
  true,
);

function clearDeliveryError(scope) {
  (scope || document)
    .querySelectorAll("[data-delivery-error]")
    .forEach((el) => {
      el.hidden = true;
    });
  (scope || document)
    .querySelectorAll(".delivery-options__option--block.has-error")
    .forEach((el) => el.classList.remove("has-error"));
}

// Populate pincode / delivery-by label and re-mount the block when it is already
// in "choose" mode. Returns false when the block's markup isn't there yet.
function syncDeliveryOptionsBlock(block) {
  let pincode = "";
  let deliveryBy = "";
  let chosenDisplay = "";
  let storedMode = "";
  try {
    pincode = localStorage.getItem("pincode") || "";
    deliveryBy = localStorage.getItem("delivery_by") || "";
    chosenDisplay = localStorage.getItem("delivery_date_display") || "";
    storedMode = localStorage.getItem("delivery_mode") || "";
  } catch (e) {
    /* no-op */
  }

  // The markup always renders Standard checked, so restore
  // the shopper's remembered mode — otherwise a re-render leaves Standard selected
  // while the previously picked scheduled date is still shown/stored.
  const chooseRadio = block.querySelector('.delivery-mode-radio[data-mode="choose"]');
  if (storedMode === "choose" && chooseRadio) {
    chooseRadio.checked = true;
    reflectDeliverySelection(block);
  } else {
    clearStoredDeliveryDate();
    chosenDisplay = "";
  }

  const pincodeEl = block.querySelector("[data-delivery-pincode]");
  if (!pincodeEl) return false;
  // Empty = the "DELIVERY TO {pincode}" header stays hidden (see the :has()
  // rule in snippets/delivery-options.liquid), so this has to run against the
  // live block every time one is rendered.
  pincodeEl.textContent = pincode;

  // "Arrives by {date}" uses the delivery badge's estimate logic. The cart has
  // no badge to publish it, so compute it here from the block's timing
  // attributes; the stored delivery_by is only a fallback for when pincode.js
  // hasn't run yet.
  // Guarded: the estimate runs before the choose sub-text and the datepicker
  // re-mount below, so a throw in here would silently disable the rest of the
  // block. Falling back to the stored date keeps it stale but visible.
  const arrivesEl = block.querySelector("[data-delivery-arrives-by]");
  if (arrivesEl) {
    let published = false;
    if (window.computeDeliveryEstimate) {
      try {
        const estimate = window.computeDeliveryEstimate(block.dataset, {
          cartContext: true,
        });
        window.publishDeliveryEstimate(estimate);
        // Cart-context estimate, kept on the block itself so a product-page
        // badge overwriting the shared delivery_by_iso can't skew
        // localStorage.standard_delivery_data.
        block.dataset.arrivesByIso = estimate.dateISO || "";
        published = true;
      } catch (e) {
        /* no-op */
      }
    }
    if (!published && !arrivesEl.textContent) arrivesEl.textContent = deliveryBy;
  }

  // Reflect a previously chosen date (survives cart:refresh) in the Choose sub-text.
  const chooseSub = block.querySelector("[data-delivery-choose-sub]");
  if (chooseSub) {
    const choosing = chooseRadio && chooseRadio.checked;
    chooseSub.textContent =
      (choosing && chosenDisplay) || chooseSub.dataset.defaultSub || "";
  }

  // Runs before the datepicker re-mount so a picker error can't skip it.
  syncArrivedBy(block);

  const chosen = block.querySelector('.delivery-mode-radio[data-mode="choose"]');
  if (chosen && chosen.checked) {
    try {
      mountDeliveryDatepicker(block);
    } catch (e) {
      /* no-op */
    }
  }
  return true;
}

// Called on load and whenever the delivery data changes.
function syncDeliveryOptions() {
  document
    .querySelectorAll("delivery-options")
    .forEach((block) => syncDeliveryOptionsBlock(block));
}

// Each block fills itself in when it lands in the document. The cart drawer and
// the cart page re-render asynchronously *after* cart:refresh is dispatched
// (CartDrawer._rerenderCart swaps innerHTML inside a setTimeout), so syncing on
// that event populated the markup that was about to be replaced and the new
// header stayed empty — i.e. hidden. Upgrading on connect removes that race.
if (!window.customElements.get("delivery-options")) {
  window.customElements.define(
    "delivery-options",
    class DeliveryOptions extends HTMLElement {
      connectedCallback() {
        // Children are already parsed when the cart swaps innerHTML; on the
        // initial (streamed) page load they may not be, so retry once.
        if (!syncDeliveryOptionsBlock(this)) {
          requestAnimationFrame(() => syncDeliveryOptionsBlock(this));
        }

        if (this._pincodeBound) return;
        this._pincodeBound = true;
        // Tapping the pincode in the header opens the pincode checker pre-filled,
        // exactly like the pincode inside the delivery badge.
        const onPincodeActivate = (e) => {
          const target =
            e.target && e.target.closest
              ? e.target.closest(".delivery-options__pincode")
              : null;
          if (!target) return;
          if (e.type === "keydown" && e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          if (!window.openPincodeChangeModal) return;
          window.openPincodeChangeModal({
            pincode: target.textContent.trim(),
            // In the drawer the modal would otherwise open behind the mini-cart.
            fromCart: this.dataset.context !== "page",
          });
        };
        this.addEventListener("click", onPincodeActivate);
        this.addEventListener("keydown", onPincodeActivate);
      }
    },
  );
}

document.addEventListener("cart:refresh", () => {
  fetchAndInitializeGiftWidget();
});
document.addEventListener("delivery:updated", syncDeliveryOptions);
document.addEventListener("DOMContentLoaded", syncDeliveryOptions);

// ------------------------------------------------------------------------------------------------------------
// ORDER NOTE CHARACTER COUNTER
// ------------------------------------------------------------------------------------------------------------
// The cart drawer and cart page both swap in fresh markup asynchronously (see the
// "delivery-options" comment above for why), so - same as that element - this counter
// initializes itself on connect rather than relying on a cart:refresh/cart:updated
// listener that could fire before (or without) the new textarea landing in the DOM.
if (!window.customElements.get("cart-note-counter")) {
  window.customElements.define(
    "cart-note-counter",
    class CartNoteCounter extends HTMLElement {
      connectedCallback() {
        const textarea =
          this.previousElementSibling && this.previousElementSibling.matches('textarea[is="cart-note"]')
            ? this.previousElementSibling
            : this.parentElement && this.parentElement.querySelector('textarea[is="cart-note"]');
        if (!textarea) return;

        this._textarea = textarea;
        this._onInput = () => this._render();
        textarea.addEventListener("input", this._onInput);
        this._render();
      }
      disconnectedCallback() {
        if (this._textarea) this._textarea.removeEventListener("input", this._onInput);
      }
      _render() {
        const max = parseInt(this._textarea.getAttribute("maxlength"), 10) || 200;
        this.textContent = `${this._textarea.value.length}/${max}`;
      }
    },
  );
}

// pdp gift info dropdown
// if (window.location.pathname.includes("products")) {
//   document.addEventListener("DOMContentLoaded", function () {
//     const pdpGiftInfoDropdown = document.querySelector(
//       ".pdp_gift_info_dropdown"
//     );
//     const pdpGiftInfoDropdownContent = document.querySelector(
//       ".pdp_gift_info_dropdown_content"
//     );
//     const pdpGiftInfoIcon = pdpGiftInfoDropdown.querySelector("svg");

//     pdpGiftInfoDropdown.addEventListener("click", function () {
//       // Toggle dropdown visibility
//       const isVisible = pdpGiftInfoDropdownContent.style.display === "block";
//       pdpGiftInfoDropdownContent.style.display = isVisible ? "none" : "block";

//       // Rotate icon if it exists
//       if (pdpGiftInfoIcon) {
//         pdpGiftInfoIcon.style.transition = "transform 0.3s ease";
//         pdpGiftInfoIcon.style.transform = isVisible
//           ? "rotate(0deg)"
//           : "rotate(180deg)";
//       }
//     });
//   });
// }
class TopBarPriceRange extends HTMLElement {
  connectedCallback() {
    const form = this.querySelector("form");

    // Update the display text when either range slider moves
    const rangeInputs = this.querySelectorAll(
      '.price-range__range-group input[type="range"]',
    );
    rangeInputs.forEach((input) => {
      input.addEventListener("input", () => this._updateDisplay());
    });

    form.addEventListener("change", (e) => {
      if (!e.target.matches('input[name^="filter."]')) return;
      this._apply(form);
    });
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      this._apply(form);
    });
  }

  _updateDisplay() {
    const symbol = this.dataset.currency || "₹";
    const ranges = this.querySelectorAll(
      '.price-range__range-group input[type="range"]',
    );
    if (ranges.length < 2) return;
    const a = parseInt(ranges[0].value, 10);
    const b = parseInt(ranges[1].value, 10);
    const minVal = Math.min(a, b);
    const maxVal = Math.max(a, b);
    const displayEls = this.querySelectorAll(".tbpr-selected-text");
    if (displayEls.length >= 2) {
      displayEls[0].textContent = symbol + minVal;
      displayEls[1].textContent = symbol + maxVal;
    } else if (displayEls.length === 1) {
      displayEls[0].textContent = symbol + minVal + " — " + symbol + maxVal;
    }
  }

  _apply(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form).entries()) {
      if (value !== "") params.append(key, value);
    }
    const url = `${window.location.pathname}?${params.toString()}`;
    this.dispatchEvent(
      new CustomEvent("facet:criteria-changed", {
        bubbles: true,
        detail: { url },
      }),
    );
  }
}
customElements.define("top-bar-price-range", TopBarPriceRange);

// Photography Consent Modal
(function () {
  function initPhotoConsentModals() {
    document.querySelectorAll('[data-consent-modal]').forEach(function (checkbox) {
      var modal = document.getElementById(checkbox.getAttribute('data-consent-modal'));
      var hiddenInput = document.getElementById(checkbox.getAttribute('data-consent-hidden'));
      if (!modal || !hiddenInput) return;

      var overlay = modal.querySelector('.photo-consent-modal__overlay');
      var okBtn = modal.querySelector('.photo-consent-modal__ok');

      function openModal() {
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
      }

      function closeModal() {
        modal.hidden = true;
        document.body.style.overflow = '';
      }

      checkbox.addEventListener('change', function () {
        hiddenInput.value = this.checked ? 'Yes' : 'No';
        if (this.checked) openModal();
      });

      okBtn.addEventListener('click', closeModal);
      overlay.addEventListener('click', closeModal);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPhotoConsentModals);
  } else {
    initPhotoConsentModals();
  }
})();

// Product bundle "Bundle Includes" carousel — chevron nav for the scrollable product row
(function () {
  function updateBundleNavState(bundle) {
    var products = bundle.querySelector('.bundle-products');
    var nav = bundle.querySelector('.bundle-nav');
    var prevBtn = bundle.querySelector('[data-bundle-scroll="prev"]');
    var nextBtn = bundle.querySelector('[data-bundle-scroll="next"]');
    if (!products || !nav) return;

    var isOverflowing = products.scrollWidth > products.clientWidth + 1;
    nav.hidden = !isOverflowing;
    if (!isOverflowing) return;

    var maxScroll = products.scrollWidth - products.clientWidth;
    if (prevBtn) prevBtn.disabled = products.scrollLeft <= 0;
    if (nextBtn) nextBtn.disabled = products.scrollLeft >= maxScroll - 1;
  }

  function updateAllBundleNavStates() {
    document.querySelectorAll('.product-bundle').forEach(updateBundleNavState);
  }

  function initBundleCarousels() {
    document.querySelectorAll('.product-bundle').forEach(function (bundle) {
      var products = bundle.querySelector('.bundle-products');
      if (!products) return;

      updateBundleNavState(bundle);
      products.addEventListener('scroll', function () {
        updateBundleNavState(bundle);
      }, { passive: true });
    });
  }

  var resizeTimeout;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(updateAllBundleNavStates, 150);
  });

  document.addEventListener('click', function (e) {
    var button = e.target.closest('[data-bundle-scroll]');
    if (!button) return;

    var bundle = button.closest('.product-bundle');
    var products = bundle && bundle.querySelector('.bundle-products');
    if (!products) return;

    var direction = button.getAttribute('data-bundle-scroll') === 'prev' ? -1 : 1;
    products.scrollBy({ left: direction * products.clientWidth * 0.8, behavior: 'smooth' });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleCarousels);
  } else {
    initBundleCarousels();
  }
})();
